import { Route } from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";
import { NotAuthGuard } from "./guards/notAuth.guard";
import { TenantAdminGuard } from "./guards/tenant-admin.guard";
import { OperationsGuard } from "./guards/operations.guard";
import { MeetingsRoutes } from "./routes/meetings.routes";
import { RequestsJoinRoutes } from "./routes/requests-join.routes";
import { ReportsRoutes } from "./routes/reports.routes";
import { TeachersRoutes } from "./routes/teachers.routes";
import { StudentsRoutes } from "./routes/students.routes";
import { AppointmentsRoutes } from "./routes/appointments.routes";
import { CoursesRoutes } from "./routes/courses.routes";
import { TeacherPipelineRoutes } from "./routes/teacher-pipeline.routes";
import { StudentPipelineRoutes } from "./routes/student-pipeline.routes";
import { TenantPipelineRoutes } from "./routes/tenant-pipeline.routes";
import { CurriculumTreeRoutes } from "./routes/curriculum-tree.routes";
import { AnnouncementsRoutes } from "./routes/announcements.routes";
import { GamificationRoutes } from "./routes/gamification.routes";
import { OrganizationSettingsRoutes } from "./routes/organization-settings.routes";
import { MaqraaAdminRoutes } from "./routes/maqraa-admin.routes";
import { SuperAdminRoutes } from "./routes/super-admin.routes";

export const routes: Route[] = [
  { path: "", redirectTo: "home", pathMatch: "full" },
  {
    path: "login",
    pathMatch: "full",
    loadComponent: () => import("../auth/pages/auth-frame/auth-frame.component").then((mod) => mod.AuthFrameComponent),
    data: { current: "login" },
    canActivate: [NotAuthGuard],
  },
  {
    path: "home",
    loadComponent: () => import("../home/home/home.component").then((mod) => mod.HomeComponent),
    pathMatch: "full",
    canActivate: [AuthGuard],
  },
  {
    path: "pages/:id",
    loadComponent: () => import("../CMS/pages/cms-page/cms-page.component").then((mod) => mod.CMSPageComponent),
    pathMatch: "full",
    canActivate: [AuthGuard],
  },
  {
    path: "notifications/student-notifications",
    loadComponent: () =>
      import("../Feature/notifications/pages/students-notifications/students-notifications.component").then(
        (mod) => mod.StudentsNotificationsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard],
  },
  {
    path: "notifications/teacher-notifications",
    loadComponent: () =>
      import("../Feature/notifications/pages/teachers-notifications/teachers-notifications.component").then(
        (mod) => mod.TeachersNotificationsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard],
  },
  // 2026-05-07 — edit-data-requests + money-transfer reverted to
  // OperationsGuard per user decision. tenant_manager handles these
  // approvals as part of day-to-day ops.
  {
    path: "modification-requests/available-requests",
    loadComponent: () =>
      import("../Feature/edit-data-requests/pages/available-requests/available-requests.component").then(
        (mod) => mod.AvailableRequestsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  {
    path: "modification-requests/rejected-requests",
    loadComponent: () =>
      import("../Feature/edit-data-requests/pages/rejected-requests/rejected-requests.component").then(
        (mod) => mod.RejectedRequestsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  {
    path: "update-version",
    loadComponent: () =>
      import("../Feature/settings/pages/update-version/update-version.component").then(
        (mod) => mod.UpdateVersionComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard],
  },
  {
    path: "payments",
    loadComponent: () =>
      import("../Feature/payments/pages/payments-report/payments-report.component").then(
        (mod) => mod.PaymentsReportComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  {
    path: "evaluations",
    loadComponent: () =>
      import("../Feature/evaluations/pages/private-session-evaluations/private-session-evaluations.component").then(
        (mod) => mod.PrivateSessionEvaluationsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  {
    // 2026-05-07 — reverted to OperationsGuard. Tenant_manager runs
    // payout review as part of operations.
    path: "money-transfer-requests",
    loadComponent: () =>
      import("../Feature/money-transfer-requests/pages/money-transfer-requests/money-transfer-requests.component").then(
        (mod) => mod.MoneyTransferRequestsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  ...AppointmentsRoutes,
  ...RequestsJoinRoutes,
  ...TeachersRoutes,
  ...StudentsRoutes,
  ...MeetingsRoutes,
  ...ReportsRoutes,
  ...CoursesRoutes,
  ...TeacherPipelineRoutes,
  ...StudentPipelineRoutes,
  ...TenantPipelineRoutes,
  ...CurriculumTreeRoutes,
  ...AnnouncementsRoutes,
  ...GamificationRoutes,
  ...OrganizationSettingsRoutes,
  ...MaqraaAdminRoutes,
  ...SuperAdminRoutes,
  // 2026-05-06 G+3 — TenantAdminGuard added (P1 follow-up from
  // X1 RBAC audit). feature-flags is a tenant-level governance
  // surface; tenant_manager + supervisor blocked at the route level.
  {
    path: "feature-flags",
    loadComponent: () =>
      import("../Feature/feature-flags/pages/feature-flags/feature-flags.component").then(
        (mod) => mod.FeatureFlagsComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, TenantAdminGuard],
  },
  // 2026-05-07 — analytics backend shipped (Slate A); reactivated
  // and gated by OperationsGuard per user decision (open to all
  // operations roles incl. supervisor for read access).
  {
    path: "analytics",
    loadComponent: () =>
      import("../Feature/analytics/pages/analytics-dashboard/analytics-dashboard.component").then(
        (mod) => mod.AnalyticsDashboardComponent
      ),
    pathMatch: "full",
    canActivate: [AuthGuard, OperationsGuard],
  },
  { path: "**", redirectTo: "home" },
];
